import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Plus, UserPlus, Search, X, Database, FileText, Check } from 'lucide-react';

/**
 * PartyCombobox: searchable selector for treasury parties.
 *
 * Empty-state offers TWO clear actions:
 *   (a) "Crea nuovo in anagrafica" → calls onCreateRequested(searchTerm)
 *   (b) "Registra solo con questo nome" → calls onUseSnapshot(searchTerm)
 *
 * Selected value can be either:
 *   - a real party (id + source)        → onChange({party_id, party_name})
 *   - a snapshot-only name              → onChange({party_id: '', party_name_snapshot: 'Nome'})
 */
export default function PartyCombobox({
  parties,           // array of {id, name, code?, vat?, source: 'erp'|'treasury'}
  value,             // {party_id, party_name_snapshot}
  partyType,         // 'client' | 'supplier'
  onChange,
  onCreateRequested, // (search) => void
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return parties.slice(0, 50);
    return parties.filter(p =>
      (p.name || '').toLowerCase().includes(term) ||
      (p.vat || '').toLowerCase().includes(term) ||
      (p.code || '').toLowerCase().includes(term)
    ).slice(0, 50);
  }, [parties, search]);

  const selectedParty = useMemo(() => {
    if (!value?.party_id) return null;
    return parties.find(p => p.id === value.party_id) || null;
  }, [parties, value]);

  const isSnapshotOnly = !value?.party_id && value?.party_name_snapshot;

  const pickParty = (p) => {
    onChange({ party_id: p.id, party_name: p.name, party_name_snapshot: null });
    setOpen(false);
    setSearch('');
  };

  const pickSnapshot = (name) => {
    onChange({ party_id: '', party_name: null, party_name_snapshot: name.trim() });
    setOpen(false);
    setSearch('');
  };

  const clear = () => {
    onChange({ party_id: '', party_name: null, party_name_snapshot: null });
    setSearch('');
  };

  const label = partyType === 'client' ? 'cliente' : 'fornitore';

  return (
    <div className="relative" ref={containerRef} data-testid={testid || 'party-combobox'}>
      {/* Display selected or input */}
      {selectedParty ? (
        <button
          type="button"
          onClick={() => { setOpen(true); setSearch(''); }}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border bg-white text-left hover:bg-slate-50 text-sm"
          data-testid={`${testid || 'party-combobox'}-selected`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Badge className={`text-[9px] ${selectedParty.source === 'erp' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'} border shrink-0`}>
              {selectedParty.source === 'erp' ? 'ERP' : 'Tesoreria'}
            </Badge>
            <span className="truncate">{selectedParty.name} {selectedParty.code && <span className="text-slate-400 text-xs">({selectedParty.code})</span>}</span>
          </div>
          <X className="w-3.5 h-3.5 text-slate-400 hover:text-red-500 shrink-0" onClick={(e) => { e.stopPropagation(); clear(); }} />
        </button>
      ) : isSnapshotOnly ? (
        <button
          type="button"
          onClick={() => { setOpen(true); setSearch(value.party_name_snapshot || ''); }}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border-2 border-amber-300 bg-amber-50 text-left hover:bg-amber-100 text-sm"
          data-testid={`${testid || 'party-combobox'}-snapshot`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[9px] shrink-0">Temporaneo</Badge>
            <span className="truncate font-medium text-amber-900">{value.party_name_snapshot}</span>
          </div>
          <X className="w-3.5 h-3.5 text-amber-700 hover:text-red-500 shrink-0" onClick={(e) => { e.stopPropagation(); clear(); }} />
        </button>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input
            placeholder={`Cerca ${label} per nome, codice o P.IVA…`}
            value={search}
            onChange={e => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            className="pl-9"
            data-testid={`${testid || 'party-combobox'}-input`}
          />
        </div>
      )}

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-80 overflow-y-auto" data-testid={`${testid || 'party-combobox'}-dropdown`}>
          {filtered.length > 0 ? (
            <ul className="py-1">
              {filtered.map(p => (
                <li key={`${p.source}-${p.id}`}>
                  <button
                    type="button"
                    onClick={() => pickParty(p)}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 flex items-center gap-2"
                    data-testid={`${testid || 'party-combobox'}-opt-${p.id}`}
                  >
                    <Badge className={`text-[9px] shrink-0 ${p.source === 'erp' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'} border`}>
                      {p.source === 'erp' ? <Database className="w-2.5 h-2.5 mr-0.5 inline" /> : <FileText className="w-2.5 h-2.5 mr-0.5 inline" />}
                      {p.source === 'erp' ? 'ERP' : 'Tesoreria'}
                    </Badge>
                    <span className="truncate flex-1">{p.name} {p.code && <span className="text-slate-400 text-xs">({p.code})</span>}</span>
                    {p.vat && <span className="text-[10px] text-slate-400 font-mono">{p.vat}</span>}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-3 space-y-2" data-testid={`${testid || 'party-combobox'}-empty`}>
              <p className="text-xs text-slate-600">
                Nessun {label} trovato {search && <>per &quot;<strong>{search}</strong>&quot;</>}.
                <br />Scegli come procedere:
              </p>
              <div className="grid grid-cols-1 gap-1.5">
                <Button
                  type="button" size="sm" variant="default"
                  onClick={() => { onCreateRequested && onCreateRequested(search); setOpen(false); }}
                  data-testid={`${testid || 'party-combobox'}-create`}
                >
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                  Crea nuovo {label} in anagrafica
                </Button>
                <Button
                  type="button" size="sm" variant="outline"
                  disabled={!search.trim()}
                  onClick={() => pickSnapshot(search)}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid={`${testid || 'party-combobox'}-snapshot-btn`}
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Registra solo con il nome {search && <span className="font-bold ml-1">&quot;{search}&quot;</span>}
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">
                💡 <strong>Solo con il nome</strong>: il nome resta visibile in fatture/calendario/dashboard. Potrai promuoverlo in anagrafica in seguito senza riscrivere.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
